import { defineStore } from 'pinia'
import { toRaw } from 'vue'

import { useOAuthStore } from 'src/stores/oauth.js'

import {
  getMastodonSocketURI,
  ProcessedWS,
  WSConnectionStatus,
} from 'src/api/websocket.js'

const ARGUMENT_MAP = {
  tag: 'tag',
  list: 'list',
}

export const TIMELINE_STREAM_MAP = {
  friends: 'user',
  public: 'public',
  tag: 'hashtag',
  list: 'list',
  dms: 'direct',
}

const retryTimeout = (multiplier) => 1000 * multiplier
const REFUSED_AFTER_FAILED_STARTS = 3
// a socket open this long is a connection, not a failed start
const HELD_AFTER = 10_000
// after it gave up: one more try now and then (a server that restarted)
const RECHECK_AFTER = 5 * 60_000
// the next socket to come: a retry, or a check after it gave up
let restartTimer = null

export class StreamStateEvent extends Event {
  original

  constructor(name, original) {
    super(name)
    this.original = original
  }
}

export class StreamErrorEvent extends Event {
  error

  constructor(error) {
    super('error', error)
    this.error = error
  }
}

export class StreamMessageEvent extends Event {
  data
  stream
  timestamp

  constructor(name, stream, data) {
    super(name)
    this.data = data
    this.stream = stream
    this.timestamp = Date.now()
  }
}

// how long to wait for the answer to the login message (Pleroma's)
const LOGIN_ANSWER_WAIT = 2000

export const useStreamingStore = defineStore('streaming', {
  state: () => ({
    socket: null,
    error: null,
    state: null,
    retryMultiplier: 1,
    retrying: false,
    // a socket opened at least once this session
    everOpened: false,
    // the last socket that opened stayed open a while
    held: false,
    // sockets in a row that closed before they opened, or soon after
    failedStarts: 0,
    subscribers: new Set(),
    subscriptions: new Map(),
    globalSubscriptions: new Set(),
  }),
  actions: {
    addSubscriber(subscriber) {
      const { stream, et } = subscriber

      if (stream) {
        if (!this.subscriptions.has(stream.name)) {
          this.subscriptions.set(stream.name, new Map())
        }

        const streamSubs = this.subscriptions.get(stream.name)

        if (streamSubs.has(stream.argument)) {
          throw new Error('Subscription already exists!')
        }

        streamSubs.set(stream.argument, subscriber)
      } else {
        this.globalSubscriptions.add(subscriber)
      }

      this.subscribers.add(subscriber)
      if (this.state === WSConnectionStatus.JOINED) {
        if (stream) {
          this.socket.subscribe(...this.getSubArgs(stream))
        }
        et.dispatchEvent(new StreamStateEvent('open'))
      }
    },
    removeSubscriber(subscriber) {
      const { stream } = subscriber

      this.subscribers.delete(subscriber)
      if (stream) {
        this.subscriptions.get(stream.name).delete(stream.argument)
      } else {
        this.globalSubscriptions.delete(subscriber)
      }

      if (stream && this.state === WSConnectionStatus.JOINED) {
        this.socket.unsubscribe(...this.getSubArgs(stream))
      }
    },
    initSocket(initial) {
      clearTimeout(restartTimer)
      if (this.socket) {
        console.error(
          "Socket already exists! This shouldn't happen! Force-closing the socket just in case",
        )
        try {
          this.socket.close()
        } catch (e) {
          console.error(
            "Error closing existing socket that shouldn't be there. Welp. Moving on.",
            e,
          )
        }
      }

      this.state = initial
        ? WSConnectionStatus.STARTING_INITIAL
        : WSConnectionStatus.STARTING

      const credentials = useOAuthStore().token
      const url = getMastodonSocketURI({ credentials })

      this.socket = ProcessedWS({
        url,
        id: 'Unified',
        credentials,
      })

      this.socket.addEventListener('pleroma:authenticated', this.onAuth)
      this.socket.addEventListener('open', this.onOpen)
      this.socket.addEventListener('close', this.onClose)
      this.socket.addEventListener('message', this.onMessage)
      this.socket.addEventListener('error', this.onError)
    },
    stopSocket() {
      if (this.state === WSConnectionStatus.CLOSED)
        console.error("Socket is already stopped! This shouldn't happen!")
      clearTimeout(restartTimer)
      this.socket?.close()
      this.socket = null
      this.state = WSConnectionStatus.CLOSED
      this.retrying = false
      this.retryMultiplier = 1
      this.failedStarts = 0
      this.held = false
      this.error = null
    },

    getSubArgs(stream) {
      const argumentKey = ARGUMENT_MAP[stream.name]
      const args = argumentKey
        ? {
            [argumentKey]: stream.argument,
          }
        : null

      return [stream.name, args]
    },
    // events of a socket the app let go of change nothing
    // (the store keeps the socket behind a reactive proxy: compare raw)
    isOld(event) {
      return !!event?.target && toRaw(event.target) !== toRaw(this.socket)
    },
    // logged in: subscribe, once per socket
    onAuth(event) {
      if (this.isOld(event)) return
      if (this.state === WSConnectionStatus.JOINED) return
      this.subscribers.forEach(({ stream, et }) => {
        et.dispatchEvent(new StreamStateEvent('authenticated'))

        if (stream) {
          this.socket.subscribe(...this.getSubArgs(stream))
        }
      })
      this.state = WSConnectionStatus.JOINED
    },
    onOpen(event) {
      if (this.isOld(event)) return
      // Mastodon and GoToSocial log in by the token in the URL and never
      // answer Pleroma's login message: without an answer, go on
      const socket = this.socket
      setTimeout(() => {
        const starting = [
          WSConnectionStatus.STARTING,
          WSConnectionStatus.STARTING_INITIAL,
        ].includes(this.state)
        if (socket && starting && toRaw(this.socket) === toRaw(socket)) {
          this.onAuth()
        }
      }, LOGIN_ANSWER_WAIT)
      this.everOpened = true
      this.held = false
      setTimeout(() => {
        if (toRaw(this.socket) !== toRaw(socket)) return
        this.held = true
        this.failedStarts = 0
      }, HELD_AFTER)
      this.retryMultiplier = 1
      this.retrying = false
      this.error = null
      this.subscribers.forEach(({ stream, et }) => {
        et.dispatchEvent(new StreamStateEvent('open'))
      })
    },
    onMessage({ data: message }) {
      if (!message) return // pings
      const { event: eventName, stream: eventStream, ...data } = message
      const [streamName, streamArgument] = eventStream ?? []

      const subscriber = this.subscriptions.get(streamName)?.get(streamArgument)

      const totalSubs = [
        ...this.globalSubscriptions.values(),
        subscriber,
      ].filter(Boolean)

      const eventData = (() => {
        switch (eventName) {
          case 'status.update':
          case 'update':
            return [data.status]
          case 'notification':
            return [data.notification]
          case 'delete':
            return [data.id]
          default:
            return data
        }
      })()

      const event = new StreamMessageEvent(
        eventName,
        { name: streamName, argument: streamArgument },
        eventData,
      )

      totalSubs.forEach(({ stream, et }) => {
        et.dispatchEvent(event)
      })
    },
    onError({ data: error }) {
      this.subscribers.forEach(({ stream, et }) => {
        et.dispatchEvent(new StreamErrorEvent(error))
      })
      this.error = error
      console.error('Error in MastoAPI websocket:', error)
    },
    // a close that reaches here is the server's (the app's own, by
    // stopSocket or a newer socket, comes from a socket it let go of): come
    // back, also after a "going away" (a server restart)
    onClose(event) {
      if (this.isOld(event)) return
      const { data: closeEvent } = event
      const { code } = closeEvent

      this.socket = null // gone; the next one comes by initSocket
      // the pollers heard "open": "close" makes them poll again
      if (!this.retrying) {
        this.subscribers.forEach(({ et }) => {
          et.dispatchEvent(new StreamStateEvent('close', closeEvent))
        })
      }

      // after a connection that held (a server restart), retry for as long
      // as it takes
      if (!this.held && ++this.failedStarts >= REFUSED_AFTER_FAILED_STARTS) {
        // the server refuses sockets (Pleroma's origin check for a hosted
        // site, a proxy without websockets) or drops them at once: polling
        // it is, silently, with one more try now and then
        console.info('Streaming not available here, polling instead')
        this.state = WSConnectionStatus.DISABLED
        this.retrying = false
        this.retryMultiplier = 1
        this.failedStarts = 0
        restartTimer = setTimeout(() => {
          this.failedStarts = REFUSED_AFTER_FAILED_STARTS - 1
          this.initSocket()
        }, RECHECK_AFTER)
      } else {
        console.warn(
          `MastoAPI websocket disconnected, restarting. CloseEvent code: ${code}`,
        )
        restartTimer = setTimeout(
          () => this.initSocket(),
          retryTimeout(this.retryMultiplier),
        )
        this.retryMultiplier += 1
        this.retrying = true
        this.state = WSConnectionStatus.ERROR
      }
    },
  },
})
